import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
  Flex,
  InputNumber,
  Popconfirm,
  Segmented,
  Tag,
  theme,
  Tooltip,
} from 'antd';
import {
  CloseCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import { selectGlobals } from 'redux/globals/selectors';
import {
  DAYS_TO_EXPIRE,
  expirationFilterOptions,
  ExpiredFilter,
  Rate,
  TrunkRate,
  useCreateTrunkRate,
  useDeleteTrunk,
  useModifyTrunkRate,
  useTrunkRates,
} from 'services/RateService';
import { Marketplace, useMarketplaces } from 'services/MarketplaceService';
import SizeIndicator from 'components/sizeIndicator';
import PaqueryTable from '@paquery-team/lib-table';
import { TableDateTimeFormat } from 'constants/dateFormats';
import { DEFAULT_PAGINATE } from 'constants/defaultValues';
import { useDebounce } from 'util/Hooks';
import { GlobalEnum } from 'types/globals';
import RateModalButton from './rateModal';

const WAIT_INTERVAL = 500;

const InterUrbanRatesList = () => {
  const globals = useSelector(selectGlobals);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [marketplace, setMarketplace] = useState<number | null>();
  const [originCdc, setOriginCdc] = useState<number | null>();
  const [destinationCdc, setDestinationCdc] = useState<number | null>();
  const [packageSize, setPackageSize] = useState<number | null>();
  const [deliveryTerm, setDeliveryTerm] = useState<number | null>();
  const [expirationFilter, setExpirationFilter] = useState(ExpiredFilter.ALL);
  const debouncedOrigin = useDebounce(originCdc, WAIT_INTERVAL);
  const debouncedDestination = useDebounce(destinationCdc, WAIT_INTERVAL);
  const { data: marketplaces } = useMarketplaces();
  const { token } = theme.useToken();
  const queries = useMemo(
    () => ({
      page,
      size,
      marketplace_id: marketplace,
      cdc_origin_id: debouncedOrigin,
      cdc_destination_id: debouncedDestination,
      expiration_status: expirationFilter,
      package_size: packageSize,
      delivery_term: deliveryTerm,
    }),
    [
      page,
      size,
      marketplace,
      debouncedOrigin,
      debouncedDestination,
      expirationFilter,
      packageSize,
      deliveryTerm,
    ],
  );
  const {
    data: trunkResponse,
    isLoading,
    isFetching,
    refetch,
  } = useTrunkRates(queries);
  const { mutateAsync: createTrunk } = useCreateTrunkRate();
  const { mutateAsync: modifyTrunk } = useModifyTrunkRate();
  const { mutate: deleteTrunk } = useDeleteTrunk();

  const marketplaceOptions = useMemo(() => {
    return marketplaces
      ? marketplaces.map((mkp: Marketplace) => ({
          label: mkp.name,
          value: mkp.id,
        }))
      : [];
  }, [marketplaces]);

  const pageable = useMemo(() => {
    return {
      pageNumber:
        trunkResponse?.data.page_number ?? DEFAULT_PAGINATE.pageNumber,
      pageSize: trunkResponse?.data.page_size ?? DEFAULT_PAGINATE.pageSize,
      totalElements:
        trunkResponse?.data.total_elements ?? DEFAULT_PAGINATE.totalElements,
    };
  }, [trunkResponse]);

  const resetPagination = () => {
    setPage(DEFAULT_PAGINATE.pageNumber);
    setSize(DEFAULT_PAGINATE.pageSize);
  };

  const handleOnPaginate = (pagination: {
    pageNumber: number;
    pageSize: number;
  }) => {
    setPage(pagination.pageNumber);
    setSize(pagination.pageSize);
  };

  const idColumn = {
    title: 'ID',
    dataIndex: 'key',
  };

  const mkpColumn = {
    title: 'Marketplace',
    render: (record: TrunkRate) => {
      if (record?.marketplace_name) {
        return record.marketplace_name;
      }

      if (record?.marketplace_id) {
        return <LoadingOutlined />;
      }

      return <MinusOutlined />;
    },
  };

  const sizeColumn = {
    title: 'Tamaño',
    dataIndex: 'package_size',
    align: 'center',
    render: (text: string) => <SizeIndicator text={text} />,
  };

  const typeColumn = {
    title: 'Plazo de entrega',
    dataIndex: 'delivery_term',
    render: (term: string) =>
      term && term !== 'undefined' ? term : <MinusOutlined />,
  };

  const originColumn = {
    title: 'Id. origen',
    dataIndex: 'cdc_origin_id',
    render: (origin: number) => origin ?? <MinusOutlined />,
  };
  const destinationColumn = {
    title: 'Id. destino',
    dataIndex: 'cdc_destination_id',
    render: (destination: number) => destination ?? <MinusOutlined />,
  };

  const validityColumn = {
    title: 'Vencimiento',
    align: 'center',
    render: (rate: Rate) => {
      let color;
      let icon;
      let title;
      const expired =
        rate.valid_until && dayjs().isAfter(dayjs(rate.valid_until).tz());
      const closeToExpiration =
        rate.valid_until &&
        dayjs()
          .add(DAYS_TO_EXPIRE, 'day')
          .isAfter(dayjs(rate.valid_until).tz());
      if (expired) {
        color = '#ff4d4f';
        icon = <CloseCircleOutlined />;
        title = 'Tarifa vencida';
      } else if (closeToExpiration) {
        color = '#ecb500';
        icon = <ExclamationCircleOutlined />;
        title = 'Próxima a vencer';
      } else {
        color = 'success';
      }
      return (
        <Tooltip title={title}>
          <Tag color={color} icon={icon} style={{ marginLeft: '8px' }}>
            {rate.valid_until ? (
              dayjs(rate.valid_until).tz().format(TableDateTimeFormat)
            ) : (
              <MinusOutlined />
            )}
          </Tag>
        </Tooltip>
      );
    },
  };

  const rateColumn = {
    title: 'Tarifa',
    render: (rate: TrunkRate) =>
      rate.rate ? (
        <p>
          {rate.currency} <b>{rate.rate}</b>
        </p>
      ) : (
        <MinusOutlined />
      ),
  };

  const costColumn = {
    title: (
      <Flex justify="space-between">
        Costo
        <Tooltip title="Refiere al monto a pagar a los drivers.">
          <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
        </Tooltip>
      </Flex>
    ),
    render: (rate: TrunkRate) =>
      rate.cost ? (
        <p>
          {rate.currency} <b>{rate.cost}</b>
        </p>
      ) : (
        <MinusOutlined />
      ),
  };

  const editColumn = {
    title: 'Editar',
    align: 'center',
    render: (record: TrunkRate) =>
      record.updated ? (
        <LoadingOutlined />
      ) : (
        <RateModalButton
          type="trunk"
          onSubmit={modifyTrunk}
          title="Edición de tarifa"
          rateId={record.id}
        />
      ),
  };

  const deleteColumn = {
    title: 'Eliminar',
    align: 'center',
    render: (record: TrunkRate) =>
      record.updated ? (
        <LoadingOutlined />
      ) : (
        <Popconfirm
          title="Eliminación de tarifa"
          description="Esta tarea no puede deshacerse"
          onConfirm={() => deleteTrunk(record)}
          okText="Confirmar"
          cancelText="Cancelar"
          placement="topRight"
          arrow={{ pointAtCenter: true }}
        >
          <DeleteOutlined style={{ color: 'red', fontSize: 18 }} />
        </Popconfirm>
      ),
  };

  const dataColumns = [
    idColumn,
    mkpColumn,
    originColumn,
    destinationColumn,
    sizeColumn,
    typeColumn,
    validityColumn,
    rateColumn,
    costColumn,
  ];

  const fullSizeColumns = [...dataColumns, editColumn, deleteColumn];
  const columnsSmallDevice = [idColumn, rateColumn];

  let mappedRates;
  if (
    trunkResponse &&
    trunkResponse.data &&
    trunkResponse.data.items &&
    trunkResponse.data.total_elements &&
    trunkResponse.data.total_elements > 0
  ) {
    mappedRates = trunkResponse.data.items.map((rate: Rate) => ({
      ...rate,
      key: rate.id,
      delivery_term:
        rate.delivery_term &&
        globals.deliveryTerm.find(
          (term: GlobalEnum) => term.value === rate.delivery_term,
        )?.name,
      package_size:
        rate.package_size &&
        globals.packages.size.find(
          (pSize: GlobalEnum) => pSize.value === rate.package_size,
        )?.name,
    }));
  }

  const deliveryTermOptions = useMemo(() => {
    const options = globals.deliveryTerm;
    return [
      {
        name: 'Sin plazo',
        value: 'undefined',
      },
      ...options,
    ];
  }, [globals.deliveryTerm]);

  const packageSizeOptions = useMemo(() => {
    const options = globals.packages.size;
    return [
      {
        name: 'Sin tamaño',
        value: 'undefined',
      },
      ...options.filter((pSize: GlobalEnum) => pSize.value !== 0),
    ];
  }, [globals.packages.size]);

  return (
    <PaqueryTable
      loading={isLoading}
      fetching={isFetching}
      header={{
        title: 'Tarifas inter-urbanas',
        refresh: refetch,
        selectors: [
          {
            // ponytail: lib-table no tipa este callback; el valor es string o number según la lista
            onChange: (value: any) => {
              resetPagination();
              setMarketplace(value !== '' ? Number(value) : null);
            },
            list: marketplaceOptions,
            placeholder: 'Marketplace',
          },
          {
            // ponytail: lib-table no tipa este callback; el valor es string o number según la lista
            onChange: (value: any) => {
              resetPagination();
              setDeliveryTerm(value);
            },
            list: deliveryTermOptions,
            placeholder: 'Plazo',
          },
          {
            // ponytail: lib-table no tipa este callback; el valor es string o number según la lista
            onChange: (value: any) => {
              resetPagination();
              setPackageSize(value);
            },
            list: packageSizeOptions,
            placeholder: 'Tamaño de paquete',
          },
        ],
        extraMainAction: (
          <>
            <InputNumber
              placeholder="Id. origen"
              style={{ minWidth: '180px', marginRight: '8px' }}
              onChange={(value) => {
                setOriginCdc(value);
              }}
              value={originCdc}
            />
            <InputNumber
              placeholder="Id. destino"
              style={{ minWidth: '180px' }}
              onChange={(value) => {
                setDestinationCdc(value);
              }}
              value={destinationCdc}
            />
            <Segmented<string>
              value={expirationFilter}
              style={{
                marginLeft: '8px',
                color: 'white',
                backgroundColor: token.colorPrimary,
              }}
              options={expirationFilterOptions}
              onChange={(value) => {
                setExpirationFilter(value as ExpiredFilter);
              }}
            />
          </>
        ),
        primaryButton: (
          <RateModalButton
            type="trunk"
            title="Creación de Tarifa"
            onSubmit={createTrunk}
            close={isFetching}
          />
        ),
      }}
      onChangePaginate={handleOnPaginate}
      dataSource={mappedRates}
      paginate={pageable}
      dataColumns={dataColumns}
      colsForMediumDevice={fullSizeColumns}
      colsForSmallDevice={columnsSmallDevice}
      colsForLargeDevice={fullSizeColumns}
    />
  );
};

export default InterUrbanRatesList;
